import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { fetchSiteDetail, Site, fetchSiteCategories, Category, fetchSiteTags, Tag } from "@/services/customerService";
import { useOripaEventList } from '@/hooks/useOripaEventList';
import EventCard from "@/components/EventCard";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";
import { useAuth } from "@/components/AuthContext";
import { Package } from "lucide-react";

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

interface OripaTopPageProps {
  siteId: number;
  requirePhoneVerification?: boolean;
}

const OripaTopPage: React.FC<OripaTopPageProps> = ({ siteId, requirePhoneVerification }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigationType = useNavigationType();
  const storageKey = `oripa-list:v1:${siteId}:${currentUser?.id ?? 'anonymous'}:${location.key}`;
  return <OripaTopContent key={storageKey} siteId={siteId} requirePhoneVerification={requirePhoneVerification}
    storageKey={storageKey} restore={navigationType === 'POP'} />;
};

const OripaTopContent: React.FC<OripaTopPageProps & { storageKey: string; restore: boolean }> = ({ siteId, requirePhoneVerification, storageKey, restore }) => {
  const { currentUser, refreshUser } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const { selectedCategory, setSelectedCategory, selectedTags, setSelectedTags,
    events, eventsLoading, nextPage, loadingMore, error, setError, loadEvents, retryEvents } = useOripaEventList(siteId, storageKey, restore);
  const [siteDetails, setSiteDetails] = useState<Site | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = listEndRef.current;
    if (!target || eventsLoading || loadingMore || error || nextPage === null) return;
    let active = true;
    const observer = new IntersectionObserver(entries => {
      if (active && entries.some(entry => entry.isIntersecting)) {
        active = false;
        observer.disconnect();
        void loadEvents(nextPage);
      }
    });
    observer.observe(target);
    return () => { active = false; observer.disconnect(); };
  }, [eventsLoading, loadingMore, error, nextPage, loadEvents]);

  const [showPhoneVerificationDialog, setShowPhoneVerificationDialog] = useState(false);

  useEffect(() => {
    const loadSiteDetails = async () => {
      try {
        const siteData = await fetchSiteDetail(siteId);
        setSiteDetails(siteData);
      } catch (err) {
        console.error(err);
        setError("店舗情報の取得に失敗しました");
      }
    };

    const loadData = async () => {
      try {
        const [categoriesData, tagsData] = await Promise.all([
          fetchSiteCategories(siteId),
          fetchSiteTags(siteId),
        ]);
        setCategories([{ id: 0, name: "ALL", slug: "all" }, ...categoriesData]);
        setTags(tagsData);
      } catch (err) {
        console.error(err);
      }
    };

    loadSiteDetails();
    loadData();
  }, [siteId]);


  // 電話番号未認証チェック（ページ表示時に1回のみ、店舗設定で必須の場合のみ）
  useEffect(() => {
    if (!currentUser) return;
    if (!requirePhoneVerification) return;

    // 既に認証済みの場合はスキップ
    if (currentUser.phone_verified) return;

    // 今回のセッションで既にスキップ済みかチェック
    const dismissedKey = `phone_verification_dismissed_${currentUser.id}`;
    const dismissed = sessionStorage.getItem(dismissedKey);

    if (!dismissed) {
      // 少し遅延させて表示（UX向上のため）
      const timer = setTimeout(() => {
        setShowPhoneVerificationDialog(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentUser, requirePhoneVerification]);

  const handlePhoneVerificationSuccess = async () => {
    setShowPhoneVerificationDialog(false);
    await refreshUser();
  };

  const handlePhoneVerificationClose = () => {
    setShowPhoneVerificationDialog(false);
    // セッション中はスキップ
    if (currentUser) {
      const dismissedKey = `phone_verification_dismissed_${currentUser.id}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }
  };

  return (
    <>
      <Helmet>
        <title>{siteDetails?.name ? `${siteDetails.name} | イベント一覧` : 'イベント一覧'}</title>
        <meta name="description" content={`${siteDetails?.name || ''}で開催中のイベント一覧`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg" data-scroll-restoration="managed">
        <div className="container mx-auto max-w-screen-lg px-4 pb-4 pt-20">
          {/* バナースライダー */}
          {(() => {
            const topBanners = siteDetails?.banners?.filter((b: { placement?: string }) => !b.placement || b.placement === 'top') || [];
            return topBanners.length > 0 && (
            <div className="mb-6">
              <div className="rounded-theme-lg border-theme shadow-theme overflow-hidden">
              <Swiper
                modules={[Autoplay, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                  el: '.banner-pagination',
                }}
                loop={topBanners.length > 1}
              >
                {topBanners.map((banner: { id: number; image_url: string | null; image_variant_urls?: Record<string, string> | null; link_url: string | null }) => (
                  <SwiperSlide key={banner.id}>
                    {banner.link_url ? (
                      <a
                        href={banner.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={banner.image_url || ''}
                          alt="バナー"
                          className="w-full h-auto object-cover"
                        />
                      </a>
                    ) : (
                      <img
                        src={banner.image_url || ''}
                        alt="バナー"
                        className="w-full h-auto object-cover"
                      />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
              </div>
              <div className="banner-pagination flex justify-center gap-1.5 mt-2" />
            </div>
            );
          })()}

          {/* カテゴリー */}
          <div className="mt-4 overflow-x-auto whitespace-nowrap scrollbar-hide">
            <nav className="flex gap-2" aria-label="Tabs">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`#${category.slug}`}
                  className={`px-4 py-2 rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm border-theme transition-all ${
                    selectedCategory?.slug === category.slug
                      ? "bg-theme-primary text-theme-button shadow-theme-sm"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCategory(category);
                  }}
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* タグ */}
          {tags.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = selectedTags.find((selectedTag) => selectedTag.slug === tag.slug);
                  return (
                    <button
                      key={tag.slug}
                      className={`px-3 py-1.5 rounded-full [font-weight:var(--theme-font-weight-button)] text-xs border-theme transition-all ${
                        isSelected
                          ? "bg-pink-400 text-black shadow-theme-sm"
                          : "bg-white text-gray-600 hover:bg-pink-100"
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedTags(
                            selectedTags.filter((selectedTag) => selectedTag.slug !== tag.slug)
                          );
                        } else {
                          setSelectedTags([...selectedTags, tag]);
                        }
                      }}
                    >
                      #{tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* イベント一覧 - オリパのみ表示 */}
          <div className="mt-6">
            {error && <div role="alert" className="mb-4 text-center text-red-600">
              <p>{error}</p>
              <button type="button" disabled={eventsLoading || loadingMore} onClick={retryEvents} className="underline">再試行</button>
            </div>}
            {eventsLoading ? (
              <div className="flex justify-center items-center w-full py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
              </div>
            ) : events.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {events.map((event) => (
                  <EventCard
                    key={event.uid}
                    id={event.id}
                    siteId={siteId.toString()}
                    siteSlug={siteDetails?.slug}
                    uid={event.uid}
                    name={event.name}
                    description={event.description}
                    imageUrl={event.thumbnail_url || "/placeholder.svg"}
                    imageVariants={event.thumbnail_variant_urls}
                    lotteryPoolsAvailableCount={event.available_stock_count ?? event.lottery_pools_available_count ?? null}
                    lotteryPoolsCount={event.total_stock_count ?? event.lottery_pools_count ?? 0}
                    status={event.status}
                    status_label={event.status_label}
                    lotteryAnimationUrl={event.lottery_animation_url}
                    prizeImages={event.prize_images}
                    prizeImageVariantUrls={event.prize_image_variant_urls}
                    gachaCost={event.gacha_cost}
                    useProductStockLottery={event.use_product_stock_lottery ?? true}
                    dailyPlayLimit={event.daily_play_limit}
                    dailyPlaysRemaining={event.daily_plays_remaining}
                    totalPlayLimit={event.total_play_limit}
                    totalPlaysRemaining={event.total_plays_remaining}
                    newCustomerOnlyDays={event.new_customer_only_days}
                    newCustomerEligible={event.new_customer_eligible}
                    newCustomerDaysRemaining={event.new_customer_days_remaining}
                    paidAmountRequirementScope={event.paid_amount_requirement_scope}
                    paidAmountRequirementMinimum={event.paid_amount_requirement_minimum}
                    paidAmountRequirementLabel={event.paid_amount_requirement_label}
                    paidAmountRequirementCurrentAmount={event.paid_amount_requirement_current_amount}
                    paidAmountRequirementEligible={event.paid_amount_requirement_eligible}
                    customerListRestricted={event.customer_list_restricted}
                    customerListEligible={event.customer_list_eligible}
                    startsAt={event.starts_at}
                    endsAt={event.ends_at}
                  />
                ))}
              </div>
            ) : error ? null : (
              <div className="flex justify-center items-center w-full py-16">
                <div className="bg-white border-4 border-dashed border-gray-300 rounded-theme-lg p-8 text-center w-full max-w-md">
                  <div className="w-16 h-16 bg-gray-100 border-2 border-gray-300 rounded-theme-lg flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 [font-weight:var(--theme-font-weight-button)] text-lg">開催中のイベントはまだありません</p>
                  <p className="text-gray-400 text-sm mt-2">新しいイベントが追加されるまでお待ちください</p>
                </div>
              </div>
            )}
            {!eventsLoading && !error && nextPage !== null && (
              <div ref={listEndRef} data-testid="event-list-end" className="mt-6 min-h-12 text-center" role="status">
                {loadingMore ? '読み込み中…' : 'スクロールして続きを表示'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 電話番号認証モーダル */}
      <PhoneVerificationDialog
        isOpen={showPhoneVerificationDialog}
        onClose={handlePhoneVerificationClose}
        onSuccess={handlePhoneVerificationSuccess}
      />
    </>
  );
};

export default OripaTopPage;
